import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { getPayoutStatusChipProps } from 'src/models/hq/franchise-fee-payout';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { ContainerBreakout } from 'src/shared/AppFrame/ContainerBreakout';
import {
  DateRangeFilter,
  Filters,
  SelectFilter,
} from 'src/shared/Filters/Filters';
import { SiteFilter } from 'src/shared/Filters/SiteFilter';
import { formatMonthYear } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';

import { useFranchiseFeePayoutPage } from './useFranchiseFeePayoutPage';

export default function PayoutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    payouts,
    payoutsTotal,
    payoutsIsLoading,
    payoutsIsError,
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useFranchiseFeePayoutPage();

  // Filter configurations
  const filterConfigs = useMemo(
    () => [
      {
        field: 'sites',
        label: t('Sites'),
        Component: SiteFilter,
      },
      {
        field: 'payoutPeriodRange',
        label: t('Date Range'),
        Component: DateRangeFilter,
      },
      {
        field: 'payoutStatus',
        label: t('Status'),
        Component: SelectFilter,
        props: {
          options: [
            { value: 'not_started', label: 'Not Started' },
            { value: 'in_progress', label: 'In Progress' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
          ],
        },
      },
    ],
    [t]
  );

  const columns = [
    {
      id: 'payoutPeriod',
      label: t('franchiseFees.payouts.payoutPeriod'),
      sortable: true,
      sortKey: 'payout_period_start', // Sort by start date
      format: (value: any, row: any) => {
        return row.payoutPeriodStart
          ? formatMonthYear(row.payoutPeriodStart, 'UTC')
          : '-';
      },
    },
    {
      id: 'payerPlatformName',
      label: t('franchiseFees.payouts.columns.payerPlatformName'),
      sortable: false,
      format: (value: any, row: any) => {
        return row.payerPlatformName || '-';
      },
    },
    {
      id: 'totalRevenueInDollars',
      label: t('franchiseFees.payouts.columns.totalRevenueInDollars'),
      sortable: true,
      sortKey: 'total_revenue_in_dollars', // Uses custom ransacker
      format: (value: any, row: any) => {
        return formatCurrency(row.totalRevenueInDollars || 0);
      },
    },
    {
      id: 'franchiseFesTotalInDollars',
      label: t('franchiseFees.payouts.columns.franchiseFesTotalInDollars'),
      sortable: true,
      sortKey: 'franchise_fees_total_in_dollars', // Uses custom ransacker
      format: (value: any, row: any) => {
        return formatCurrency(row.franchiseFesTotalInDollars || 0);
      },
    },
    {
      id: 'adjustmentsTotalInDollars',
      label: t('franchiseFees.payouts.columns.adjustmentsTotalInDollars'),
      sortable: true,
      sortKey: 'adjustments_total_in_dollars', // Uses custom ransacker
      format: (value: any, row: any) => {
        const amount = row.adjustmentsTotalInDollars || 0;
        return (
          <Typography
            color={
              amount < 0
                ? 'error.main'
                : amount > 0
                ? 'success.main'
                : 'inherit'
            }
          >
            {formatCurrency(amount)}
          </Typography>
        );
      },
    },
    {
      id: 'amountOwedInDollars',
      label: t('franchiseFees.payouts.columns.amountOwedInDollars'),
      sortable: true,
      sortKey: 'amount_owed_in_dollars', // Uses custom ransacker
      format: (value: any, row: any) => {
        return formatCurrency(row.amountOwedInDollars || 0);
      },
    },
    {
      id: 'payoutStatus',
      label: t('franchiseFees.payouts.columns.payoutStatus'),
      sortable: true,
      sortKey: 'payout_status', // Use the actual database column name
      format: (value: any, row: any) => {
        const { color, label } = getPayoutStatusChipProps(row.payoutStatus);
        return <Chip color={color} label={label} size="small" />;
      },
    },
  ];

  if (payoutsIsError) {
    return (
      <>
        <AppFrameConfig
          id="franchise-fee-payouts"
          containerMaxWidth={'xl'}
          title="Franchise Fee Payouts"
        />
        <ContainerBreakout>
          <Card sx={{ mb: 6, p: 4 }}>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <Typography>Error loading payouts. Please try again.</Typography>
          </Card>
        </ContainerBreakout>
      </>
    );
  }

  return (
    <>
      <AppFrameConfig
        id="franchise-fee-payouts"
        containerMaxWidth={'xl'}
        title="Franchise Fee Payouts"
      />

      {/* Filters */}
      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <Scroller
          direction="row"
          spacing={2}
          hideScrollbar
          sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
        >
          <Filters
            filters={filterConfigs}
            params={params}
            updateParam={updateParam}
            updateFilterParam={updateFilterParam}
            getFilterParamValue={getFilterParamValue}
            resetFilterParams={resetFilterParams}
          />
        </Scroller>
      </SectionToolbar>

      <ContainerBreakout>
        <Card sx={{ mb: 6 }}>
          <DataTable
            columns={columns}
            rows={payouts}
            noResultsText="No payouts found"
            loading={payoutsIsLoading}
            sortBy={params.sortBy}
            setSortBy={(value) => updateParam('sortBy', value)}
            sortDir={params.sortDir}
            setSortDir={(value) => updateParam('sortDir', value)}
            totalItems={payoutsTotal}
            itemsPerPage={params.perPage}
            onClickRow={(row) => {
              navigate(`/franchise-fees/payouts/${row.id}`);
            }}
          />
          <SearchParamsPagination
            total={payoutsTotal || 0}
            itemsPerPage={params.perPage}
            params={params}
            updateParam={updateParam}
          />
        </Card>
      </ContainerBreakout>
    </>
  );
}
